// core/UI/context/ThemeContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Theme, ThemeName, themeLoaders } from '@config/themes/ITheme';
import crashTheme from '@config/themes/crash.theme'; // default theme object
import { gameThemeMap } from '@config/game-theme.map'; // slug -> ThemeName map

// Flatten helper (same as yours)
const flattenTheme = (theme: Theme, prefix = ''): Record<string, string> => {
  let flat: Record<string, string> = {};
  for (const key in theme) {
    const newPrefix = prefix ? `${prefix}-${key}` : key;
    const value = (theme as any)[key];
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flat, flattenTheme(value, newPrefix));
    } else {
      flat[newPrefix] = value;
    }
  }
  return flat;
};

interface ThemeContextType {
  theme: Theme;
  themeName: ThemeName;
  setTheme: (name: ThemeName) => Promise<void>;
  setThemeBySlug: (slug: string | undefined) => Promise<void>;
  toggleTheme: () => void;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [themeName, setThemeName] = useState<ThemeName>('crash');
  const [theme, setThemeState] = useState<Theme>(crashTheme);
  const [isLoading, setIsLoading] = useState(false);

  // Load saved theme on mount
  useEffect(() => {
    const saved = localStorage.getItem('theme') as ThemeName | null;
    if (saved && themeLoaders[saved]) {
      setTheme(saved).catch(console.error);
    }
    // if no saved theme, ensure CSS variables exist for default
    else {
      const flat = flattenTheme(crashTheme);
      Object.entries(flat).forEach(([k, v]) =>
        document.documentElement.style.setProperty(`--${k}`, v)
      );
    }
  }, []);

  const setTheme = async (name: ThemeName) => {
    if (!themeLoaders[name]) {
      console.warn(`[Theme] loader missing for "${name}"`);
      return;
    }
    if (isLoading) return;

    try {
      setIsLoading(true);
      localStorage.setItem('theme', name);
      setThemeName(name);

      const module = await themeLoaders[name]();
      if (!module?.default) {
        console.error(`[Theme] module for "${name}" has no default export`);
        return;
      }
      setThemeState(module.default);

      // set CSS variables
      const flatTheme = flattenTheme(module.default);
      const root = document.documentElement;
      Object.entries(flatTheme).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
      // console.log(`[Theme] applied: ${name}`);
    } catch (err) {
      console.error('[Theme] failed to load:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Normalize slug and map to ThemeName
  const normalizeSlug = (s: string) => s.trim().toLowerCase();

  const setThemeBySlug = async (slug: string | undefined) => {
    if (!slug) {
      // use default from map if provided
      const def = (gameThemeMap as any)['default'] as ThemeName | undefined;
      if (def) await setTheme(def);
      return;
    }
    const normalized = normalizeSlug(slug);
    const mapped = (gameThemeMap as any)[normalized] as ThemeName | undefined;
    const themeToApply = mapped ?? (gameThemeMap as any)['default'] ?? 'crash';
    console.log(`[Theme] slug "${slug}" -> theme "${themeToApply}"`);
    await setTheme(themeToApply);
  };

  const toggleTheme = () => {
    const keys = Object.keys(themeLoaders) as ThemeName[];
    const currentIndex = keys.indexOf(themeName);
    const nextTheme = keys[(currentIndex + 1) % keys.length];
    setTheme(nextTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeName,
        setTheme,
        setThemeBySlug,
        toggleTheme,
        isLoading,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
